import FeatureGuard, { isFeatureAvailable } from "@components/featureGuard/FeatureGuard"
import { CellSetterV2 } from "@components/table/BodyContentLayoutNL"
import { useAppSelector } from "@redux/app/hooks"
import { getCurrentCompany } from "@redux/features/currentCompany/currentCompanySlice"
import type { PortalMode } from "@redux/features/userData/userDataSlice"
import type { ColumnDef } from "@tanstack/react-table"
import { PortalFeature } from "api"
import type { ReactElement } from "react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import type { NormalizedFishLogEvent } from "./fishLogEvents"
import { summarizeFishLogInterval } from "./fishLogEvents"
import { isAquacultureTimelineCompany } from "./isAquacultureTimelineCompany"
import type { EventTableEntry, FuelConsumptionTimelineTableItem } from "./OperationsTimelineContent"
import { useFishLogEventsWithLookback } from "./queries/useFishLogEventsQuery"

export type TimelineViewMode = "opmode" | "fishmode"

export type OpModeIntervalEvent = {
	opMode: string
	start: Date
	end: Date
	eventData: EventTableEntry
}

export { isAquacultureTimelineCompany }

export function useAquacultureTimelineData(args: {
	plantId: number | undefined
	currentCompany: string | undefined
	devFeatures: string[]
	portalMode?: PortalMode
	gte: Date
	lt: Date
}): {
	isEnabled: boolean
	fishLogEvents: NormalizedFishLogEvent[]
} {
	const isEnabled =
		args.currentCompany !== undefined &&
		isAquacultureTimelineCompany(args.currentCompany) &&
		isFeatureAvailable(
			undefined,
			PortalFeature.AQUACULTURE,
			args.currentCompany,
			args.devFeatures,
			args.portalMode
		)

	const { normalizedEvents } = useFishLogEventsWithLookback({
		plantId: args.plantId,
		company: args.currentCompany,
		gte: args.gte,
		lt: args.lt,
		enabled: isEnabled,
	})

	return useMemo(
		() => ({
			isEnabled,
			fishLogEvents: normalizedEvents,
		}),
		[isEnabled, normalizedEvents]
	)
}

type InferAccessorType<T extends keyof FuelConsumptionTimelineTableItem> =
	FuelConsumptionTimelineTableItem[T]

function dateDiffInHoursAndMinutes(a: Date, b: Date): string {
	const msPerHour = 1000 * 60 * 60
	const aTimestamp = new Date(a).getTime()
	const bTimestamp = new Date(b).getTime()

	const hours = Math.floor((bTimestamp - aTimestamp) / msPerHour)
	const minutes = Math.floor(((bTimestamp - aTimestamp) % msPerHour) / (1000 * 60))
	return `${hours}h${minutes < 10 ? `0` : ``}${minutes}`
}

function calcFuelDeviationFromEventData(eventData: EventTableEntry[]): {
	percent: number
	fuel: number
} {
	const deviationDenominator = eventData.reduce(
		(prev, curr) =>
			curr.event.fuelBaselineM3 === 0 || curr.event.fuelBaselineM3 === null
				? prev
				: curr.event.fuelBaselineM3 + prev,
		0
	)
	const fuelDeviation = eventData.reduce(
		(prev, curr) =>
			curr.event.fuelActualM3 === null || curr.event.fuelBaselineM3 === null
				? prev
				: curr.event.fuelActualM3 - curr.event.fuelBaselineM3 + prev,
		0
	)

	return {
		percent: (100 * fuelDeviation) / (deviationDenominator === 0 ? 1 : deviationDenominator),
		fuel: fuelDeviation,
	}
}

function summarizeOpModesForInterval(
	events: OpModeIntervalEvent[],
	start: Date | string,
	end: Date | string
): {
	labels: string[]
	primaryLabel: string
	eventData: EventTableEntry[]
	totalFuel: number
} {
	const startMs = new Date(start).getTime()
	const endMs = new Date(end).getTime()
	if (Number.isNaN(startMs) || Number.isNaN(endMs) || startMs >= endMs) {
		return {
			labels: [],
			primaryLabel: "--",
			eventData: [],
			totalFuel: 0,
		}
	}

	const labels = new Set<string>()
	const overlapByLabel = new Map<string, number>()
	const eventData: EventTableEntry[] = []
	let totalFuel = 0

	for (const event of events) {
		const overlapStart = Math.max(startMs, new Date(event.start).getTime())
		const overlapEnd = Math.min(endMs, new Date(event.end).getTime())
		if (overlapStart >= overlapEnd) {
			continue
		}

		labels.add(event.opMode)
		overlapByLabel.set(
			event.opMode,
			(overlapByLabel.get(event.opMode) ?? 0) + (overlapEnd - overlapStart)
		)
		eventData.push(event.eventData)
		totalFuel += event.eventData.event.plantTotalFuelM3
	}

	const primaryLabel =
		[...overlapByLabel.entries()].sort((a, b) => {
			if (b[1] !== a[1]) {
				return b[1] - a[1]
			}
			return a[0].localeCompare(b[0])
		})[0]?.[0] ?? "--"

	const labelArray = [...labels]
	const orderedLabels =
		primaryLabel !== "--"
			? [
					primaryLabel,
					...labelArray
						.filter((l) => l !== primaryLabel)
						.sort((a, b) => a.localeCompare(b)),
				]
			: labelArray.sort((a, b) => a.localeCompare(b))

	return {
		labels: orderedLabels,
		primaryLabel,
		eventData,
		totalFuel,
	}
}

export function useAquacultureTimelineColumns(args: {
	isEnabled: boolean
	viewMode: TimelineViewMode
	hasOpModeData: boolean
}): ColumnDef<FuelConsumptionTimelineTableItem>[] {
	const { t } = useTranslation()

	return useMemo(() => {
		if (args.isEnabled === true && args.viewMode === "fishmode") {
			return [
				{
					accessorKey: "fishMode",
					header: t("Fish mode"),
					cell: (info) =>
						CellSetterV2({
							content: {
								type: "text",
								text: info.getValue() as InferAccessorType<"fishMode">,
							},
							index: info.cell.id,
						}),
				},
				// Fish-only vessels have no op-modes, so these columns would just show "--".
				...(args.hasOpModeData === true
					? [
							{
								accessorKey: "primaryOpMode",
								header: t("Primary op mode"),
								cell: (info) =>
									CellSetterV2({
										content: {
											type: "text",
											text: info.getValue() as InferAccessorType<"primaryOpMode">,
										},
										index: info.cell.id,
									}),
							} satisfies ColumnDef<FuelConsumptionTimelineTableItem>,
							{
								accessorKey: "opMode",
								header: t("Op modes during fish mode"),
								cell: (info) =>
									CellSetterV2({
										content: {
											type: "text",
											text: info.getValue() as InferAccessorType<"opMode">,
										},
										index: info.cell.id,
									}),
							} satisfies ColumnDef<FuelConsumptionTimelineTableItem>,
						]
					: []),
			]
		}

		return [
			{
				accessorKey: "opMode",
				header: t("MODE"),
				cell: (info) =>
					CellSetterV2({
						content: {
							type: "text",
							text: info.getValue() as InferAccessorType<"opMode">,
						},
						index: info.cell.id,
					}),
			},
			...(args.isEnabled === true
				? [
						{
							accessorKey: "fishPrimaryMode",
							header: t("Primary fish mode"),
							cell: (info) =>
								CellSetterV2({
									content: {
										type: "text",
										text: info.getValue() as InferAccessorType<"fishPrimaryMode">,
									},
									index: info.cell.id,
								}),
						} satisfies ColumnDef<FuelConsumptionTimelineTableItem>,
						{
							accessorKey: "fishMode",
							header: t("Fish mode"),
							cell: (info) =>
								CellSetterV2({
									content: {
										type: "text",
										text: info.getValue() as InferAccessorType<"fishMode">,
									},
									index: info.cell.id,
								}),
						} satisfies ColumnDef<FuelConsumptionTimelineTableItem>,
					]
				: []),
		]
	}, [args.isEnabled, args.viewMode, args.hasOpModeData, t])
}

export function buildAquacultureTimelineRows(args: {
	baseRows: FuelConsumptionTimelineTableItem[]
	rawOpModeEvents: OpModeIntervalEvent[]
	fishLogEvents: NormalizedFishLogEvent[]
	viewMode: TimelineViewMode
	rangeStart: Date
	rangeEnd: Date
	canUserSeeBaseline: boolean
}): FuelConsumptionTimelineTableItem[] {
	const opModeRows = args.baseRows.map((entry) => {
		const fishSummary = summarizeFishLogInterval(args.fishLogEvents, entry.date, entry.dateEnd)

		return {
			...entry,
			primaryOpMode: entry.opMode,
			fishMode: fishSummary.labels.length > 0 ? fishSummary.labels.join(", ") : "--",
			fishPrimaryMode: fishSummary.primaryLabel,
		}
	})

	if (args.viewMode === "opmode") {
		return opModeRows
	}

	const selectedRangeStartMs = args.rangeStart.getTime()
	const selectedRangeEndMs = args.rangeEnd.getTime()

	return args.fishLogEvents
		.map((fishEvent, index) => {
			const clippedStartMs = Math.max(selectedRangeStartMs, fishEvent.startMs)
			const clippedEndMs = Math.min(selectedRangeEndMs, fishEvent.endMs)
			if (clippedStartMs >= clippedEndMs) {
				return undefined
			}

			const opModeSummary = summarizeOpModesForInterval(
				args.rawOpModeEvents,
				new Date(clippedStartMs),
				new Date(clippedEndMs)
			)
			// Drop fish events with no overlapping op-mode data only for vessels that actually
			// have op-modes. Fish-only vessels (UniSea operations module, e.g. Sølvtrans
			// well-boats) have no op-modes at all, so show their fish log events standalone.
			if (opModeSummary.eventData.length === 0 && args.rawOpModeEvents.length > 0) {
				return undefined
			}

			return {
				id: index,
				eventData: opModeSummary.eventData,
				date: new Date(clippedStartMs),
				dateEnd: new Date(clippedEndMs),
				duration: dateDiffInHoursAndMinutes(
					new Date(clippedStartMs),
					new Date(clippedEndMs)
				),
				opMode: opModeSummary.labels.length > 0 ? opModeSummary.labels.join(", ") : "--",
				primaryOpMode: opModeSummary.primaryLabel,
				fishMode: fishEvent.label,
				fishPrimaryMode: fishEvent.label,
				totalFuel: opModeSummary.totalFuel,
				deviation:
					args.canUserSeeBaseline === true && opModeSummary.eventData.length > 0
						? calcFuelDeviationFromEventData(opModeSummary.eventData)
						: { percent: 0, fuel: 0 },
			}
		})
		.filter((item): item is FuelConsumptionTimelineTableItem => item !== undefined)
		.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
}

export function AquacultureTimelineViewSwitch({
	viewMode,
	setViewMode,
}: {
	viewMode: TimelineViewMode
	setViewMode: (viewMode: TimelineViewMode) => void
}): ReactElement | null {
	const { t } = useTranslation()
	const currentCompany = useAppSelector(getCurrentCompany)

	if (isAquacultureTimelineCompany(currentCompany) !== true) {
		return null
	}

	return (
		<FeatureGuard featureName={PortalFeature.AQUACULTURE}>
			<div className="border-dipai-text-muted flex h-8 overflow-hidden rounded-sm border-1">
				<button
					type="button"
					className={`px-3 text-xs font-medium hover:cursor-pointer ${
						viewMode === "opmode"
							? "bg-dipai-secondary-800 text-white"
							: "text-dipai-text-secondary bg-white"
					}`}
					onClick={() => setViewMode("opmode")}
				>
					{t("Op mode events")}
				</button>
				<button
					type="button"
					className={`border-l-dipai-text-muted border-l-1 px-3 text-xs font-medium hover:cursor-pointer ${
						viewMode === "fishmode"
							? "bg-dipai-secondary-800 text-white"
							: "text-dipai-text-secondary bg-white"
					}`}
					onClick={() => setViewMode("fishmode")}
				>
					{t("Fish log events")}
				</button>
			</div>
		</FeatureGuard>
	)
}
